import React, { useState } from "react";
import "./FlowCalculator.css";

const nozzleOptions = Array.from({ length: 30 }, (_, index) => index + 1);
const plantPlaceOptions = [0, 1, 2, 3, 4, 5];
const sprayOptions = [0, 1, 5, 10, 15, 30, 60];
const pauseOptions = [0, 10, 30, 60, 120, 300, 600, 900];
const numberFormat = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 3 });
const antiDripProductUrl = "https://www.mrs-shop.com/m.r.s.-anti-tropf-keramik-nebelduese-mit-t-steckverteiler";
const microHeadProductUrl = "https://www.mrs-shop.com/m.r.s.-micro-nebelduese-duesenkopf-bohrung-0-2-mm";
export const manufacturerFlowReferences = Object.freeze([
  Object.freeze({ id: "mrs-anti-drip-15-20", pressure: "15–20 bar", calculationPressure: "20 bar", flowMlPerMinute: 30, product: "Anti-Tropf-Keramikdüse mit T-Verteiler · 0,2 mm", sourceUrl: antiDripProductUrl, comparison: false }),
  Object.freeze({ id: "mrs-15-20", pressure: "15–20 bar", calculationPressure: "20 bar", flowMlPerMinute: 40, product: "anderer Micro-Düsenkopf · 0,2 mm", sourceUrl: microHeadProductUrl, comparison: true }),
  Object.freeze({ id: "mrs-70", pressure: "70 bar", calculationPressure: "70 bar", flowMlPerMinute: 75, product: "anderer Micro-Düsenkopf · 0,2 mm", sourceUrl: microHeadProductUrl, comparison: true }),
]);

function decimal(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  if (!/^(?:\d+(?:[.,]\d*)?|[.,]\d+)$/.test(text)) return null;
  const parsed = Number(text.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** A steady repeating timer calculation, never a pump command or a nozzle model. */
export function calculateFlowEstimate({ nozzles, flowMlPerMinute, spraySeconds, pauseSeconds }) {
  const values = {
    nozzles: decimal(nozzles),
    flow: decimal(flowMlPerMinute),
    spray: decimal(spraySeconds),
    pause: decimal(pauseSeconds),
  };
  const errors = {};
  if (!Number.isInteger(values.nozzles) || values.nozzles < 1 || values.nozzles > 30) {
    errors.nozzles = "Bitte 1 bis 30 Düsen auswählen.";
  }
  if (String(flowMlPerMinute ?? "").trim() === "") {
    errors.flow = "Bitte den gemessenen Durchfluss je Düse in ml/min eintragen.";
  } else if (values.flow === null) {
    errors.flow = "Durchfluss: bitte eine endliche Zahl ab 0 eingeben, mit Komma oder Punkt.";
  }
  if (values.spray === null) errors.spray = "Sprühzeit: bitte eine endliche Zahl ab 0 Sekunden eingeben.";
  if (values.pause === null) errors.pause = "Pause: bitte eine endliche Zahl ab 0 Sekunden eingeben.";
  const cycleSeconds = values.spray === null || values.pause === null ? null : values.spray + values.pause;
  if (cycleSeconds !== null && (cycleSeconds <= 0 || !Number.isFinite(cycleSeconds))) {
    errors.cycle = "Sprühzeit und Pause müssen zusammen größer als 0 und endlich sein.";
  }
  if (Object.keys(errors).length) return { status: "incomplete", errors };
  const dutyFraction = values.spray / cycleSeconds;
  // A single spray uses only its ON time. Pause changes the repeating average,
  // never the volume delivered during that individual spray.
  const millilitersPerSprayPerNozzle = values.flow * (values.spray / 60);
  const millilitersPerSpray = values.nozzles * millilitersPerSprayPerNozzle;
  const litersPerHour = values.nozzles * (values.flow / 1000) * 60 * dutyFraction;
  const litersPerDay = litersPerHour * 24;
  if (![millilitersPerSprayPerNozzle, millilitersPerSpray, litersPerHour, litersPerDay].every(Number.isFinite)) {
    return { status: "incomplete", errors: { calculation: "Die Werte sind für diese Berechnung zu groß. Bitte kleinere Werte eingeben." } };
  }
  return { status: "ready", errors: {}, millilitersPerSpray, millilitersPerSprayPerNozzle, litersPerHour, litersPerDay, dutyFraction };
}

/** Equal distribution of an existing flow estimate, not measured plant uptake. */
export function calculatePlantFlowEstimate({ litersPerHour, channels, plantsPerChannel }) {
  const channelCount = decimal(channels);
  const occupiedPlaces = decimal(plantsPerChannel);
  const errors = {};
  if (!Number.isInteger(channelCount) || channelCount < 1 || channelCount > 30) {
    errors.channels = "Bitte eine ganze Anzahl von 1 bis 30 Pflanzkanälen eingeben.";
  }
  if (!Number.isInteger(occupiedPlaces) || occupiedPlaces < 0 || occupiedPlaces > 5) {
    errors.plantsPerChannel = "Bitte 0 bis 5 belegte Pflanzenplätze je Kanal auswählen.";
  }
  if (typeof litersPerHour !== "number" || !Number.isFinite(litersPerHour) || litersPerHour < 0) {
    errors.flow = "Bitte zuerst die Durchflussberechnung vervollständigen.";
  }
  if (Object.keys(errors).length) return { status: "incomplete", errors };
  const totalPlants = channelCount * occupiedPlaces;
  if (totalPlants === 0) return { status: "empty", errors: {}, totalPlants, millilitersPerPlantHour: null, millilitersPerPlantDay: null };
  const millilitersPerPlantHour = (litersPerHour / totalPlants) * 1000;
  const millilitersPerPlantDay = millilitersPerPlantHour * 24;
  if (![millilitersPerPlantHour, millilitersPerPlantDay].every(Number.isFinite)) {
    return { status: "incomplete", errors: { calculation: "Die Menge je Pflanze ist für diese Berechnung zu groß." } };
  }
  return { status: "ready", errors: {}, totalPlants, millilitersPerPlantHour, millilitersPerPlantDay };
}

function formatVolume(value) {
  return value > 0 && value < .001 ? "< 0,001" : numberFormat.format(value);
}

function TimeChoice({ id, label, presets, choice, custom, onChoice, onCustom, error }) {
  const customSelected = choice === "custom";
  return (
    <div className="feeder-flow-field">
      <label htmlFor={id}>{label} (Sekunden)</label>
      <select id={id} value={choice} onChange={(event) => onChoice(event.target.value)}>
        {presets.map((seconds) => <option key={seconds} value={seconds}>{seconds} s</option>)}
        <option value="custom">Eigener Wert …</option>
      </select>
      {customSelected && (
        <>
          <label className="feeder-flow-custom-label" htmlFor={`${id}-custom`}>Eigene {label} (Sekunden)</label>
          <input
            id={`${id}-custom`} type="text" inputMode="decimal" autoComplete="off"
            value={custom} onChange={(event) => onCustom(event.target.value)} required
            aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}
          />
        </>
      )}
      {error && <p id={`${id}-error`} className="feeder-flow-error">{error}</p>}
    </div>
  );
}

export default function FlowCalculator({ id = "feeder-flow" }) {
  const [nozzles, setNozzles] = useState("3");
  const [channels, setChannels] = useState("1");
  const [plantsPerChannel, setPlantsPerChannel] = useState("5");
  const [flow, setFlow] = useState("");
  const [flowBasis, setFlowBasis] = useState("mrs-anti-drip-15-20");
  const [sprayChoice, setSprayChoice] = useState("10");
  const [pauseChoice, setPauseChoice] = useState("300");
  const [sprayCustom, setSprayCustom] = useState("");
  const [pauseCustom, setPauseCustom] = useState("");
  const reference = manufacturerFlowReferences.find(item => item.id === flowBasis);
  const result = calculateFlowEstimate({
    nozzles, flowMlPerMinute: reference ? reference.flowMlPerMinute : flow,
    spraySeconds: sprayChoice === "custom" ? sprayCustom : sprayChoice,
    pauseSeconds: pauseChoice === "custom" ? pauseCustom : pauseChoice,
  });
  const ready = result.status === "ready";
  const plantResult = calculatePlantFlowEstimate({ litersPerHour: result.litersPerHour, channels, plantsPerChannel });
  const flowInvalid = flow.trim() !== "" && Boolean(result.errors.flow);

  return (
    <section className="feeder-flow" aria-labelledby={`${id}-title`}>
      <div className="feeder-flow-heading">
        <div>
          <p className="feeder-flow-kicker">Dein Aufbau · deine Rechnung</p>
          <h2 id={`${id}-title`}>Wie viel Lösung wird versprüht?</h2>
          <p>Berechne die Menge pro Sprühstoß und den geplanten mittleren Durchsatz für einen wiederholten Sprüh- und Pausentakt.</p>
        </div>
        <span className="feeder-flow-badge">Rechenhilfe · keine Anlagensteuerung</span>
      </div>

      <div className="feeder-flow-layout">
        <div className="feeder-flow-inputs">
          <div className="feeder-flow-field">
            <label htmlFor={`${id}-nozzles`}>Düsen insgesamt</label>
            <select id={`${id}-nozzles`} value={nozzles} onChange={(event) => setNozzles(event.target.value)} aria-describedby={`${id}-nozzles-note`}>
              {nozzleOptions.map((count) => <option key={count} value={count}>{count} {count === 1 ? "Düse" : "Düsen"}</option>)}
            </select>
            <p id={`${id}-nozzles-note`} className="feeder-flow-help">Startwert: eine Kammer mit drei Düsen. Wähle die Gesamtzahl – Kammern werden nicht zusätzlich multipliziert.</p>
          </div>
          <div className="feeder-flow-field">
            <label htmlFor={`${id}-basis`}>Durchflussgrundlage</label>
            <select id={`${id}-basis`} value={flowBasis} onChange={(event) => setFlowBasis(event.target.value)} aria-describedby={`${id}-basis-note`}>
              <option value="measured">Eigene Messung · tatsächlicher Betriebsdruck</option>
              {manufacturerFlowReferences.map(item => <option key={item.id} value={item.id}>{item.comparison ? "Vergleich: anderer Micro-Düsenkopf" : "Anti-Tropf-Keramikdüse"} · {item.calculationPressure} · {item.flowMlPerMinute} ml/min</option>)}
            </select>
            {reference ? (
              <p className="feeder-flow-reference">Herstellerangabe: <strong>{reference.flowMlPerMinute} ml/min je Düse bei {reference.pressure}</strong>. {reference.product}{reference.comparison ? " – nur Modellvergleich" : ""}. Kein Messwert deines Aufbaus.</p>
            ) : (
              <>
                <label className="feeder-flow-custom-label" htmlFor={`${id}-measured`}>Gemessener Durchfluss je Düse (ml/min)</label>
                <input
                  id={`${id}-measured`} type="text" inputMode="decimal" autoComplete="off" required
                  value={flow} onChange={(event) => setFlow(event.target.value)}
                  aria-invalid={flowInvalid} aria-describedby={`${id}-flow-note${flowInvalid ? ` ${id}-flow-error` : ""}`}
                />
                <p id={`${id}-flow-note`} className="feeder-flow-help">Messwert während des Sprühens beim tatsächlichen Betriebsdruck, nicht der bereits über Pausen gemittelte Wert. Bei mehreren Düsen: Mittelwert je Düse. Komma oder Punkt als Dezimalzeichen, keine Tausendertrennzeichen.</p>
                {flowInvalid && <p className="feeder-flow-error" id={`${id}-flow-error`}>{result.errors.flow}</p>}
              </>
            )}
            <p id={`${id}-basis-note`} className="feeder-flow-help">Quelle: <a href={reference?.sourceUrl || antiDripProductUrl} target="_blank" rel="noopener noreferrer">{reference?.comparison ? "M.R.S. Micro-Nebeldüse · anderer Düsenkopf 0,2 mm" : "M.R.S. Anti-Tropf-Keramikdüse mit T-Steckverteiler · 0,2 mm"}</a>. {reference?.comparison ? "Diese Werte gehören nicht zur Anti-Tropf-Düse." : "Die bestätigte Anti-Tropf-Düse ist bis maximal 20 bar angegeben."} Keine Durchflussreferenz bei 25 bar.</p>
          </div>
          <TimeChoice id={`${id}-spray`} label="Sprühzeit" presets={sprayOptions} choice={sprayChoice} custom={sprayCustom} onChoice={setSprayChoice} onCustom={setSprayCustom} error={result.errors.spray} />
          <TimeChoice id={`${id}-pause`} label="Pause" presets={pauseOptions} choice={pauseChoice} custom={pauseCustom} onChoice={setPauseChoice} onCustom={setPauseCustom} error={result.errors.pause} />
          <p className="feeder-flow-preset-note">10 s Sprühen und 300 s Pause sind nur veränderbare Beispielwerte, keine Anbauempfehlung. Die Pause ist die ausgeschaltete Zeit nach jedem Sprühen.</p>
          <fieldset className="feeder-flow-plant-inputs">
            <legend>Pflanzenbelegung</legend>
            <div className="feeder-flow-field">
              <label htmlFor={`${id}-channels`}>Anzahl Pflanzkanäle</label>
              <input
                id={`${id}-channels`} type="number" inputMode="numeric" min="1" max="30" step="1" required
                value={channels} onChange={(event) => setChannels(event.target.value)}
                aria-invalid={Boolean(plantResult.errors.channels)}
                aria-describedby={`${id}-plants-note${plantResult.errors.channels ? ` ${id}-channels-error` : ""}`}
              />
              {plantResult.errors.channels && <p id={`${id}-channels-error`} className="feeder-flow-error">{plantResult.errors.channels}</p>}
            </div>
            <div className="feeder-flow-field">
              <label htmlFor={`${id}-plant-places`}>Belegte Pflanzenplätze je Kanal</label>
              <select
                id={`${id}-plant-places`} value={plantsPerChannel} onChange={(event) => setPlantsPerChannel(event.target.value)}
                aria-invalid={Boolean(plantResult.errors.plantsPerChannel)} aria-describedby={`${id}-plants-note`}
              >
                {plantPlaceOptions.map(count => <option key={count} value={count}>{count} von 5 Plätzen</option>)}
              </select>
            </div>
            <p id={`${id}-plants-note`} className="feeder-flow-help">Fünf Plätze pro AeroFlo-Kanal; gleiche Belegung in allen Kanälen angenommen. Den Messsystemplatz nicht als Pflanze zählen. Die Kanalzahl verändert die Düsen-Gesamtzahl nicht.</p>
          </fieldset>
        </div>

        <div className="feeder-flow-results" role="status" aria-live="polite" aria-atomic="true">
          <p className="feeder-flow-result-label">Geplanter Brutto-Durchsatz</p>
          <p className="feeder-flow-result-basis">Grundlage: {reference ? `Herstellerangabe · ${reference.flowMlPerMinute} ml/min je Düse · Rechendruck ${reference.calculationPressure}${reference.pressure !== reference.calculationPressure ? ` (Herstellerbereich ${reference.pressure})` : ""} · ${reference.product}${reference.comparison ? " · nur Modellvergleich" : ""}` : "Eigene Messung · tatsächlicher Betriebsdruck"}.</p>
          <p>Umlaufmenge, kein Verbrauchswert: Dieselbe Lösung kann mehrfach durch die Düsen fließen.</p>
          {ready ? (
            <>
              <dl>
                <div><dt>Pro Sprühstoß · alle Düsen</dt><dd><strong data-testid="flow-spray-total">{formatVolume(result.millilitersPerSpray)}</strong><span>ml</span></dd></div>
                <div><dt>Pro Sprühstoß · je Düse</dt><dd><strong data-testid="flow-spray-nozzle">{formatVolume(result.millilitersPerSprayPerNozzle)}</strong><span>ml</span></dd></div>
                <div><dt>Liter pro Stunde</dt><dd><strong data-testid="flow-hour">{formatVolume(result.litersPerHour)}</strong><span>L/h</span></dd></div>
                <div><dt>Liter pro Tag · 24 Stunden</dt><dd><strong data-testid="flow-day">{formatVolume(result.litersPerDay)}</strong><span>L/Tag</span></dd></div>
                <div className="feeder-flow-plant-result">
                  <dt>Mittlere Umlaufmenge je Pflanze</dt>
                  <dd><strong data-testid="flow-plant-hour">{plantResult.status === "ready" ? formatVolume(plantResult.millilitersPerPlantHour) : "—"}</strong><span>ml/Pflanze/h</span><strong data-testid="flow-plant-day">{plantResult.status === "ready" ? formatVolume(plantResult.millilitersPerPlantDay) : "—"}</strong><span>ml/Pflanze/Tag</span></dd>
                  {plantResult.status === "incomplete" ? <p>{Object.values(plantResult.errors).join(" ")}</p> : <p><span data-testid="flow-total-plants">{numberFormat.format(plantResult.totalPlants)}</span> Pflanzen insgesamt · {channels} {channels === "1" ? "Kanal" : "Kanäle"} × {plantsPerChannel} belegte Plätze.</p>}
                  {plantResult.status === "empty" && <p>Ohne belegte Pflanzenplätze ist keine Menge je Pflanze berechenbar.</p>}
                  <p>Berechnete Durchschnitts-Umlaufmenge, keine gemessene Wasseraufnahme oder garantierte Verteilung auf einzelne Pflanzen.</p>
                </div>
              </dl>
              <p>Die Menge pro Sprühstoß hängt vom gewählten Durchfluss und der Sprühzeit ab, nicht von der Pause. „Je Düse“ ist bei einem gemittelten Messwert ebenfalls ein Mittelwert.</p>
              <p className="feeder-flow-duty">Sprühanteil am Takt: {formatVolume(result.dutyFraction * 100)} %.</p>
              <p>Der Tageswert setzt denselben Takt über volle 24 Stunden voraus.</p>
            </>
          ) : (
            <p className="feeder-flow-missing">{Object.values(result.errors).join(" ")}</p>
          )}
          {reference?.id === "mrs-70" && <p className="feeder-flow-comparison-warning">70 bar liegen außerhalb des Bereichs der verlinkten <a href="https://www.mrs-shop.com/whisper-powerpump-ultimate-22-bar.html" target="_blank" rel="noopener noreferrer">22-bar-Pumpe</a>; nur Herstellervergleich eines anderen Düsenkopfs, kein Wert für die Anti-Tropf-Düse.</p>}
        </div>
      </div>

      <details className="feeder-flow-explanation">
        <summary>Was wird berechnet – und was nicht?</summary>
        <p>ml pro Sprühstoß je Düse = ml/min je Düse × Sprühzeit in Sekunden ÷ 60. ml pro Sprühstoß insgesamt = Menge je Düse × Düsenanzahl. Die Pause geht in diese beiden Mengen nicht ein.</p>
        <p>Liter/Stunde = Düsenanzahl × (ml/min je Düse ÷ 1.000) × 60 × Sprühzeit ÷ (Sprühzeit + Pause). Liter/Tag = Liter/Stunde × 24.</p>
        <p>Gesamtpflanzen = Pflanzkanäle × belegte Pflanzenplätze je Kanal. ml/Pflanze/Stunde = Liter/Stunde × 1.000 ÷ Gesamtpflanzen. ml/Pflanze/Tag = ml/Pflanze/Stunde × 24. Bei null Pflanzen gibt es keinen Wert je Pflanze. Die rechnerisch gleichmäßige Aufteilung beschreibt weder die tatsächliche Benetzung noch die Wasseraufnahme.</p>
        <p>Dies ist der Brutto-Durchsatz durch die Düsen, nicht der Wasserverbrauch: Im Kreislauf kann dieselbe Lösung mehrfach versprüht werden. Der Wert beschreibt weder Tankbedarf noch eine zugesicherte Pumpenförderleistung.</p>
        <p>Die Rechnung setzt den gewählten Durchfluss während des Sprühens als gleichbleibend voraus. Druckaufbau, Nachlaufen, unterschiedliche Düsen und Verluste sind nicht eigens modelliert. Sie bestätigt weder Tröpfchengröße, Pflanzenbedarf noch Sterilität und sendet keine Befehle an die Anlage.</p>
        <p>Aus 0,2 mm Düsenöffnung und 25 bar allein lässt sich der Durchfluss nicht bestimmen. Die Herstellerwerte validieren keinen 25-bar-Durchfluss; sie werden nicht umgerechnet oder interpoliert.</p>
        <p>Aufbaugrenze: Die bestätigte Anti-Tropf-Keramikdüse mit T-Verteiler ist mit maximal 20 bar angegeben. Die <a href="https://www.mrs-shop.com/whisper-powerpump-ultimate-22-bar.html" target="_blank" rel="noopener noreferrer">Whisper PowerPump Ultimate</a> ist mit maximal 22 bar angegeben. Nutzerbeobachtung: etwa 25 bar mit 12 Düsen – oberhalb dieser Grenze, kein freigegebener Sollwert und kein Durchflussmesswert. Die 70-bar-Referenz ist mit dieser Pumpe nicht erreichbar und gehört zu einem anderen Micro-Düsenkopf; alle Referenzen sind keine Pumpenfreigabe.</p>
      </details>
    </section>
  );
}
